import { HR, NavbarMenuItem, useLanguage } from "@/lib/main";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { closeTransitionMs } from "./NavbarMenuItemSubmenu.constants";
import { getSideLayout } from "./NavbarMenuItemSubmenu.helpers";
import {
  createSubmenuContainerStyle,
  createSubmenuListStyle,
} from "./NavbarMenuItemSubmenu.styles";
import {
  initialSideLayout,
  NavbarMenuItemSubmenuProps,
  SideLayoutType,
} from "./NavbarMenuItemSubmenu.types";

export default function NavbarMenuItemSubmenu({
  anchorRef,
  direction,
  isHashLinks,
  isHovered,
  isLastItem = false,
  navbarMenuItem,
  onMouseEnter,
  onMouseLeave,
}: NavbarMenuItemSubmenuProps) {
  const { textDirection } = useLanguage();
  const isSideDirection = direction === "side";

  const submenuRef = useRef<HTMLUListElement>(null);
  const hasOpened = useRef(false);

  const [isClosing, setIsClosing] = useState(false);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const [sideLayout, setSideLayout] =
    useState<SideLayoutType>(initialSideLayout);

  useEffect(() => {
    if (isHovered && submenuRef.current) {
      hasOpened.current = true;

      const fullHeight = submenuRef.current.scrollHeight;

      if (direction === "down") {
        setHeight(fullHeight);
      }

      if (direction === "side") {
        setWidth(submenuRef.current.scrollWidth);
        setHeight(
          sideLayout.maxHeight > 0
            ? Math.min(fullHeight, sideLayout.maxHeight)
            : fullHeight,
        );
      }
    } else if (hasOpened.current) {
      setIsClosing(true);
      setHeight(0);
      setWidth(0);

      const timeout = window.setTimeout(() => {
        setIsClosing(false);
      }, closeTransitionMs);

      return () => {
        window.clearTimeout(timeout);
      };
    }
  }, [direction, isHovered, sideLayout.maxHeight]);

  useEffect(() => {
    if (!isSideDirection || (!isHovered && !isClosing)) return;

    const updateSidePosition = () => {
      const anchorLi = anchorRef?.current;
      if (!anchorLi) return;

      const anchorRect = anchorLi.getBoundingClientRect();
      const contentWidth = submenuRef.current?.scrollWidth ?? 0;
      const measuredSubmenuWidth = Math.max(contentWidth, anchorRect.width);
      const submenuWidth = isHovered ? measuredSubmenuWidth : width;

      if (isHovered && measuredSubmenuWidth !== width) {
        setWidth(measuredSubmenuWidth);
      }

      const nextSideLayout = getSideLayout({
        anchorLeft: anchorRect.left,
        anchorRight: anchorRect.right,
        isLastItem,
        submenuWidth,
        textDirection,
        top: anchorRect.top,
        viewportHeight: window.innerHeight,
        viewportWidth: window.innerWidth,
      });

      setSideLayout(nextSideLayout);
    };

    const frameId = window.requestAnimationFrame(updateSidePosition);

    window.addEventListener("resize", updateSidePosition);
    window.addEventListener("scroll", updateSidePosition, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updateSidePosition);
      window.removeEventListener("scroll", updateSidePosition, true);
    };
  }, [
    anchorRef,
    isSideDirection,
    isClosing,
    isHovered,
    isLastItem,
    textDirection,
    width,
  ]);

  const submenuContainerStyle = createSubmenuContainerStyle({
    direction,
    height,
    isClosing,
    isHovered,
    sideLayout,
    textDirection,
    width,
  });

  const submenuListStyle = createSubmenuListStyle({
    direction,
    sideLayout,
  });

  const isVisible = isHovered || isClosing;

  const submenuContent = (
    <div
      inert={!isVisible ? true : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={submenuContainerStyle}
    >
      <ul ref={submenuRef} style={submenuListStyle} role="menu">
        {navbarMenuItem?.submenu?.map((item) =>
          item.kind === "hr" ? (
            <li key={item.id}>
              <HR />
            </li>
          ) : (
            <NavbarMenuItem
              key={item.id}
              direction="side"
              isHashLinks={isHashLinks}
              isLastItem={isLastItem}
              navbarMenuItem={item}
            />
          ),
        )}
      </ul>
    </div>
  );

  if (isSideDirection && typeof document !== "undefined") {
    return createPortal(submenuContent, document.body);
  }

  return submenuContent;
}
